var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { computed, watch, shallowRef, defineComponent } from 'vue';
import classNames from '../_util/classNames';
import PropTypes from '../_util/vue-types';
import VcMentions from '../vc-mentions';
import { mentionsProps as baseMentionsProps } from '../vc-mentions/src/mentionsProps';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import { flattenChildren, getOptionProps } from '../_util/props-util';
import { FormItemInputContext, useInjectFormItemContext } from '../form/FormItemContext';
import omit from '../_util/omit';
import { optionProps, optionOptions } from '../vc-mentions/src/Option';
import { getStatusClassNames, getMergedStatus } from '../_util/statusUtils';
import useStyle from './style';
import { useProvideOverride } from '../menu/src/OverrideContext';
import useVariant from '../config-provider/hooks/useVariant';
import warning from '../_util/warning';
import Spin from '../spin';
import devWarning from '../vc-util/devWarning';
import { booleanType, functionType, someType, stringType } from '../_util/type';
import CloseCircleFilled from '@ant-design/icons-vue/CloseCircleFilled';
function loadingFilterOption() {
    return true;
}
const getMentions = (value = '', config = {}) => {
    const { prefix = '@', split = ' ' } = config;
    const prefixList = Array.isArray(prefix) ? prefix : [prefix];
    return value
        .split(split)
        .map((str = '') => {
        let hitPrefix = null;
        prefixList.some(prefixStr => {
            const startStr = str.slice(0, prefixStr.length);
            if (startStr === prefixStr) {
                hitPrefix = prefixStr;
                return true;
            }
            return false;
        });
        if (hitPrefix !== null) {
            return {
                prefix: hitPrefix,
                value: str.slice(hitPrefix.length),
            };
        }
        return null;
    })
        .filter((entity) => !!entity && !!entity.value);
};
export const mentionsProps = () => (Object.assign(Object.assign({}, baseMentionsProps), { loading: { type: Boolean, default: undefined }, onFocus: {
        type: Function,
    }, onBlur: {
        type: Function,
    }, onSelect: {
        type: Function,
    }, onChange: {
        type: Function,
    }, onPressenter: {
        type: Function,
    }, 'onUpdate:value': {
        type: Function,
    }, notFoundContent: PropTypes.any, defaultValue: String, id: String, status: String, bordered: booleanType(true), 
    /** Prefer over `bordered` (antd ≥ 5.13). */
    variant: stringType(), 
    /** Show clear icon (antd ≥ 5.13). */
    allowClear: someType([Boolean, Object]), 
    /** Triggered when clear icon is clicked (antd ≥ 5.20). */
    onClear: functionType() }));
const Mentions = defineComponent({
    compatConfig: { MODE: 3 },
    name: 'AMentions',
    inheritAttrs: false,
    props: mentionsProps(),
    slots: Object,
    setup(props, { slots, emit, attrs, expose }) {
        var _a, _b, _c;
        // =================== Warning =====================
        if (process.env.NODE_ENV !== 'production') {
            devWarning(!flattenChildren(((_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots)) || []).length, 'Mentions', '`Mentions.Option` is deprecated. Please use `options` instead.');
        }
        const { prefixCls, renderEmpty, direction } = useConfigInject('mentions', props);
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const variant = useVariant(props);
        const focused = shallowRef(false);
        const vcMentions = shallowRef(null);
        const value = shallowRef((_c = (_b = props.value) !== null && _b !== void 0 ? _b : props.defaultValue) !== null && _c !== void 0 ? _c : '');
        const formItemContext = useInjectFormItemContext();
        const formItemInputContext = FormItemInputContext.useInject();
        const mergedStatus = computed(() => getMergedStatus(formItemInputContext.status, props.status));
        useProvideOverride({
            prefixCls: computed(() => `${prefixCls.value}-menu`),
            mode: computed(() => 'vertical'),
            selectable: computed(() => false),
            onClick: () => { },
            validator: ({ mode }) => {
                // Warning if use other mode
                warning(!mode || mode === 'vertical', 'Mentions', `mode="${mode}" is not supported for Mentions's Menu.`);
            },
        });
        watch(() => props.value, val => {
            value.value = val;
        });
        const handleFocus = (e) => {
            focused.value = true;
            emit('focus', e);
        };
        const handleBlur = (e) => {
            focused.value = false;
            emit('blur', e);
            formItemContext.onFieldBlur();
        };
        const handleSelect = (...args) => {
            emit('select', ...args);
            focused.value = true;
        };
        const handleChange = (val) => {
            if (props.value === undefined) {
                value.value = val;
            }
            emit('update:value', val);
            emit('change', val);
            formItemContext.onFieldChange();
        };
        const handleClear = (e) => {
            var _a;
            (_a = props.onClear) === null || _a === void 0 ? void 0 : _a.call(props, e);
            handleChange('');
        };
        const getNotFoundContent = () => {
            const notFoundContent = props.notFoundContent;
            if (notFoundContent !== undefined) {
                return notFoundContent;
            }
            if (slots.notFoundContent) {
                return slots.notFoundContent();
            }
            return renderEmpty('Select');
        };
        const getOptions = () => {
            var _a;
            return flattenChildren(((_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots)) || []).map(item => {
                var _a, _b;
                return Object.assign(Object.assign({}, getOptionProps(item)), { label: (_b = (_a = item.children) === null || _a === void 0 ? void 0 : _a.default) === null || _b === void 0 ? void 0 : _b.call(_a) });
            });
        };
        const focus = () => {
            vcMentions.value.focus();
        };
        const blur = () => {
            vcMentions.value.blur();
        };
        expose({ focus, blur });
        const mentionsfilterOption = computed(() => props.loading ? loadingFilterOption : props.filterOption);
        return () => {
            const { disabled, getPopupContainer, rows = 1, id = formItemContext.id.value } = props, restProps = __rest(props, ["disabled", "getPopupContainer", "rows", "id"]);
            const { hasFeedback, feedbackIcon } = formItemInputContext;
            const { class: className } = attrs, otherAttrs = __rest(attrs, ["class"]);
            const otherProps = omit(restProps, [
                'defaultValue',
                'onUpdate:value',
                'prefixCls',
                'bordered',
                'variant',
                'allowClear',
                'onClear',
            ]);
            const mergedVariant = variant.value;
            const { allowClear } = props;
            const needClear = !!allowClear && !disabled && !!value.value;
            const clearIcon = typeof allowClear === 'object' && allowClear ? allowClear.clearIcon : undefined;
            const mergedClassName = classNames({
                [`${prefixCls.value}-disabled`]: disabled,
                [`${prefixCls.value}-focused`]: focused.value,
                [`${prefixCls.value}-rtl`]: direction.value === 'rtl',
                [`${prefixCls.value}-borderless`]: mergedVariant === 'borderless',
                [`${prefixCls.value}-filled`]: mergedVariant === 'filled',
                [`${prefixCls.value}-underlined`]: mergedVariant === 'underlined',
            }, getStatusClassNames(prefixCls.value, mergedStatus.value), !hasFeedback && !allowClear && className, hashId.value);
            const mentionsProps = Object.assign(Object.assign(Object.assign(Object.assign({ prefixCls: prefixCls.value }, otherProps), { disabled, direction: direction.value, filterOption: mentionsfilterOption.value, getPopupContainer, options: props.loading
                    ? [
                        {
                            value: 'ANTDV_SEARCHING',
                            disabled: true,
                            label: <Spin size="small"/>,
                        },
                    ]
                    : props.options || getOptions(), class: mergedClassName }), otherAttrs), { rows, onChange: handleChange, onSelect: handleSelect, onFocus: handleFocus, onBlur: handleBlur, ref: vcMentions, value: value.value, id });
            const mentions = (<VcMentions {...mentionsProps} dropdownClassName={hashId.value} v-slots={{ notFoundContent: getNotFoundContent, option: slots.option }}></VcMentions>);
            if (hasFeedback || allowClear) {
                return wrapSSR(<div class={classNames(`${prefixCls.value}-affix-wrapper`, getStatusClassNames(`${prefixCls.value}-affix-wrapper`, mergedStatus.value, hasFeedback), {
                        [`${prefixCls.value}-affix-wrapper-borderless`]: mergedVariant === 'borderless',
                        [`${prefixCls.value}-affix-wrapper-filled`]: mergedVariant === 'filled',
                        [`${prefixCls.value}-affix-wrapper-underlined`]: mergedVariant === 'underlined',
                    }, className, hashId.value)}>
            {mentions}
            <span class={`${prefixCls.value}-suffix`}>
              {allowClear && (<span class={classNames(`${prefixCls.value}-clear-icon`, {
                            [`${prefixCls.value}-clear-icon-hidden`]: !needClear,
                            [`${prefixCls.value}-clear-icon-has-suffix`]: hasFeedback,
                        })} role="button" onClick={handleClear} onMousedown={e => e.preventDefault()}>
                  {clearIcon !== null && clearIcon !== void 0 ? clearIcon : <CloseCircleFilled />}
                </span>)}
              {hasFeedback && feedbackIcon}
            </span>
          </div>);
            }
            return wrapSSR(mentions);
        };
    },
});
/* istanbul ignore next */
export const MentionsOption = defineComponent(Object.assign(Object.assign({ compatConfig: { MODE: 3 } }, optionOptions), { name: 'AMentionsOption', props: optionProps }));
export default Object.assign(Mentions, {
    Option: MentionsOption,
    getMentions,
    install: (app) => {
        app.component(Mentions.name, Mentions);
        app.component(MentionsOption.name, MentionsOption);
        return app;
    },
});
